import './headerPair.scss';

import cn from 'classnames';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, NavLink } from 'react-router-dom';

import { useIsDark } from '@/shared/theme';

import { ApiPairs, ApiProjects } from 'api';
import { getGeckoterminalToken } from 'entities/token';
import { UDPairModal } from 'modals';
import ProjectListIconPairs from 'shared/components/PlatformIcon';
import { ROUTE_PATHS } from 'shared/consts/routes';
import { useTypedDispatch, useTypedSelector } from 'store';
import {
  setPairError,
  setPairLoading,
  setSelectedCexPair,
  setSelectedDexPair,
  setSelectedSolPair,
} from 'store/slices/pairs';
import { IProject } from 'types/project';
import { Tooltip } from 'ui/Tooltip';
import { geckoterminalLink } from 'utils';
import { calculateScanLink } from 'utils/calculates';
import { getPairLinkedBots } from 'utils/getPairLinkedBots';
import { EExchange, ENetwork, IDexName, web3Configs } from 'web3';

import { PairHeaderSkeleton } from './PairHeaderSkeleton';
import { resolveSolPair } from './solPairRoute';

import { CogIcon } from 'assets/icons';
import geckoterminalLogo from 'assets/images/geckoterminal_logo.png';

interface IDexPairHeaderProps {
  pairId: number;
  projectId: number;
}

const capitalizeWord = (value: string) => (value ? value[0].toUpperCase() + value.slice(1) : '');

const formatExchangeName = (value: string) => {
  const [rawName, chain] = value.split(':');

  const name = rawName
    .split('_')
    .map(part => (/^v\d+$/i.test(part) ? part.toUpperCase() : capitalizeWord(part)))
    .join(' ');

  return chain ? `${name} (${chain.toUpperCase()})` : name;
};

export default function SolPairHeader({ pairId, projectId }: IDexPairHeaderProps) {
  const dispatch = useTypedDispatch();
  const isDark = useIsDark();

  const solPair = useTypedSelector(store => store.pairs.selectedSolPair);
  const projects = useTypedSelector(store => store.projects.projects);
  const isAdmin = useTypedSelector(store => store.auth.isAdmin);

  const [project, setProject] = useState<IProject | null>(null);

  useEffect(() => {
    const setup = async () => {
      try {
        dispatch(setSelectedCexPair(null));
        dispatch(setSelectedDexPair(null));
        dispatch(setSelectedSolPair(null));
        dispatch(setPairLoading(true));
        dispatch(setPairError(false));

        const cachedProject = projects?.find(p => p.id === projectId);
        const pair = await resolveSolPair({
          cachedPair: cachedProject?.solPairs.find(p => p.id === pairId),
          getPairsByProject: ApiPairs.getPairsByProjectId,
          pairId,
          projectId,
        });
        if (!pair) {
          dispatch(setPairError(true));
          return;
        }
        const [base, _] = pair.symbol.split('/');

        // TODO: replace hardcoded data with API calls when backend is ready
        const [
          activeProject,
          // activePair,
          quoteToken,
          baseToken,
        ] = await Promise.all([
          ApiProjects.getProjectById(projectId),
          // ApiPairs.getDexPairById(pairId),
          getGeckoterminalToken({
            network: ENetwork.solana,
            tokenAddress: 'So11111111111111111111111111111111111111112',
          }),
          getGeckoterminalToken({
            network: ENetwork.solana,
            // @ts-ignore
            tokenAddress: pair.mint,
          }),
        ]);

        const activePair = {
          isSuccess: true,
          data: {
            // @ts-ignore
            pool_id: pair.Address,
            created_at: pair.created_at,
            dex: pair.dex as IDexName,
            fee: 0.0025,
            id: pair.id,
            notes: '',
            project_id: projectId,
            symbol: pair.symbol,
            network: ENetwork.solana,
            token_base: {
              // @ts-ignore
              address: pair.mint,
              decimals: (quoteToken?.data?.attributes.decimals ?? pair.id === 10002) ? 9 : 6,
              id: 2,
              network: ENetwork.solana,
              symbol: base,
              price_usd: baseToken?.data?.attributes.price_usd || '0.0',
            },
            token_fee: {
              address: 'So11111111111111111111111111111111111111112',
              symbol: 'SOL',
              decimals: quoteToken?.data?.attributes.decimals ?? 9,
              id: 1,
              network: ENetwork.solana,
              price_usd: quoteToken?.data?.attributes.price_usd || '0.0',
            },
            token_quote: {
              address: 'So11111111111111111111111111111111111111112',
              symbol: 'SOL',
              decimals: quoteToken?.data?.attributes.decimals ?? 9,
              id: 1,
              network: ENetwork.solana,
              price_usd: quoteToken?.data?.attributes.price_usd || '0.0',
            },
            updated_at: pair.updated_at,
            swap_gas_limit: 500000,
          },
        };

        if (activeProject.isSuccess && activePair.isSuccess) {
          setProject({
            created_at: activeProject.data.created_at,
            id: activeProject.data.id,
            name: activeProject.data.name,
            cexPairs: [],
            dexPairs: [],
            solPairs: [],
            updated_at: cachedProject?.updated_at ?? activeProject.data.created_at,
            notes: activeProject.data.notes ?? '',
          });

          if (activePair.data.network === 'solana') {
            dispatch(setSelectedSolPair(activePair.data));
          }

          await getPairLinkedBots({
            pairId: activePair.data.id,
            dispatch,
            exchange: EExchange.sol,
          });
        } else {
          dispatch(setPairError(true));
        }
      } catch (error) {
        console.log(error);
        dispatch(setPairError(true));
      } finally {
        dispatch(setPairLoading(false));
      }
    };

    setup();
  }, [dispatch, projects, pairId, projectId]);

  const [updateModalOpened, setUpdateModalOpened] = useState<boolean>(false);

  const onOpenUpdateModalOpened = useCallback(() => {
    setUpdateModalOpened(true);
  }, []);

  const onCloseUpdateModalOpened = useCallback(() => {
    setUpdateModalOpened(false);
  }, []);

  const solPairAddress = solPair?.address ?? solPair?.pool_id;

  const geckoterminalLinkUrl = useMemo(
    () =>
      solPairAddress && solPair
        ? geckoterminalLink({ network: solPair.network, pairAddress: solPairAddress })
        : undefined,
    [solPair, solPairAddress],
  );

  if (!project || !solPair) {
    return <PairHeaderSkeleton />;
  }

  return (
    <div className="mm-header__header-pair">
      {project && solPair && (
        <>
          <div className="mm-header__header-pair__info">
            <div className="pair-name">
              <motion.a
                href={calculateScanLink({
                  address: solPair.token_base.address,
                  network: solPair.network,
                  addressType: 'token',
                })}
                target="_blank"
                rel="noreferrer"
                whileHover={{ scale: 1.1 }}
              >
                {solPair.token_base.symbol}
              </motion.a>
              <span>/</span>
              <motion.a
                href={calculateScanLink({
                  address: solPair.token_quote.address,
                  network: solPair.network,
                  addressType: 'token',
                })}
                target="_blank"
                rel="noreferrer"
                whileHover={{ scale: 1.1 }}
              >
                {solPair.token_quote.symbol}
              </motion.a>
            </div>
            {isAdmin && (
              <motion.div
                whileHover={{ rotate: 180, scale: 1.3 }}
                transition={{ duration: 0.3 }}
                className="pair-settings"
                onClick={onOpenUpdateModalOpened}
              >
                <CogIcon />
              </motion.div>
            )}
            <NavLink
              to={generatePath(ROUTE_PATHS.project, { id: project.id.toString() })}
              className="project-name"
            >
              {project.name}
            </NavLink>
            <Tooltip
              trigger={
                <div
                  className={cn(
                    'header-trading-pair-logo-header',
                    solPair.dex.includes('uniswap') ? 'uniswap' : solPair.dex,
                    { 'bg-white': isDark, 'bg-bg-primary': !isDark },
                  )}
                >
                  <ProjectListIconPairs platform={solPair.dex} className="exchange-icon" />
                </div>
              }
              content={formatExchangeName(solPair.dex)}
              options={{ placement: 'top', offset: 8 }}
            />
          </div>
          <div className="mm-header__header-pair__bottom">
            {solPairAddress && (
              <a
                className={cn('scan-link rounded-full p-[2px]', { 'bg-white': isDark })}
                target="_blank"
                rel="noreferrer"
                href={calculateScanLink({
                  address: solPairAddress,
                  addressType: 'address',
                  network: solPair.network,
                })}
              >
                <img src={web3Configs[solPair.network].scanLogo} alt="" />
              </a>
            )}
            {geckoterminalLinkUrl && (
              <a
                className="trade-pair-link"
                target="_blank"
                rel="noreferrer"
                href={geckoterminalLinkUrl}
              >
                <img src={geckoterminalLogo} alt="geckoterminal" />
              </a>
            )}
            {solPair.notes && isAdmin && <span>{solPair.notes}</span>}
          </div>
          {updateModalOpened && (
            <UDPairModal
              exchange={EExchange.sol}
              onClose={onCloseUpdateModalOpened}
              onOpen={onOpenUpdateModalOpened}
              pairId={solPair.id}
              projectId={project.id}
              pairSymbol={solPair.symbol}
              initialNotes={solPair.notes ?? ''}
            />
          )}
        </>
      )}
    </div>
  );
}
